import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as latexReports from '../api/latexReports';
import { checkAssignmentPATStatus } from '../api/patReports';
import PATReportsPanel from './PATReportsPanel';
import { getAuthToken } from '../utils/authToken';

const LaTeXReportsPanel = ({ assignmentId, assignmentTitle }) => {
  const [reports, setReports] = useState({ database_reports: [], github_reports: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPATAssignment, setIsPATAssignment] = useState(false);
  const [checkingPAT, setCheckingPAT] = useState(true);
  const token = getAuthToken();

  useEffect(() => {
    const checkPATAndFetchReports = async () => {
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        setCheckingPAT(false);
        return;
      }

      try {
        // First check if this is a PAT assignment
        const patStatusResponse = await checkAssignmentPATStatus(token, assignmentId);
        const isPatAssignment = patStatusResponse.data.is_pat_assignment;
        setIsPATAssignment(isPatAssignment);
        setCheckingPAT(false);

        // If it's not a PAT assignment, fetch regular LaTeX reports
        if (!isPatAssignment) {
          try {
            const response = await latexReports.getAssignmentReports(token, assignmentId);
            setReports(response.data);
          } catch (err) {
            setError('Failed to load reports');
            console.error('Error fetching reports:', err);
          }
        }
      } catch (err) {
        console.error('Error checking PAT status:', err);
        setCheckingPAT(false);
        // If PAT check fails, assume it's a regular assignment and try to load LaTeX reports
        try {
          const response = await latexReports.getAssignmentReports(token, assignmentId);
          setReports(response.data);
        } catch (reportErr) {
          setError('Failed to load reports');
          console.error('Error fetching reports:', reportErr);
        }
      } finally {
        setLoading(false);
      }
    };

    if (assignmentId) {
      checkPATAndFetchReports();
    }
  }, [assignmentId, token]);

  const handleDownload = async (reportId, type) => {
    if (!token) {
      console.error('Authentication required');
      return;
    }

    try {
      const response = type === 'pdf' 
        ? await latexReports.downloadPdf(token, reportId)
        : await latexReports.downloadLatex(token, reportId);
      
      // Open download link in new window
      window.open(response.request.responseURL, '_blank');
    } catch (err) {
      console.error(`Failed to download ${type}:`, err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (checkingPAT) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Report Generation</h3>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  // If this is a PAT assignment, show the PAT Reports Panel instead
  if (isPATAssignment) {
    return <PATReportsPanel assignmentId={assignmentId} assignmentTitle={assignmentTitle} token={token} />;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">LaTeX Reports</h3>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  const totalReports = reports.database_reports.length + reports.github_reports.length;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">LaTeX Reports ({totalReports})</h3>
        <Link
          to={`/assignments/${assignmentId}/generate-report`}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          + Generate Report
        </Link>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {totalReports === 0 ? (
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h4 className="mt-2 text-sm font-medium text-gray-900">No reports yet</h4>
          <p className="mt-1 text-sm text-gray-500">
            Generate your first LaTeX marking report for this assignment
          </p>
          <div className="mt-6">
            <Link
              to={`/assignments/${assignmentId}/generate-report`}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Generate First Report
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Database Reports */}
          {reports.database_reports.map((report) => (
            <div key={report.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{report.name}</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Generated {formatDate(report.created_at)} • {report.total_students} students
                  </p>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      report.generation_status === 'completed' 
                        ? 'bg-green-100 text-green-800'
                        : report.generation_status === 'failed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {report.generation_status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                
                {report.is_completed && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDownload(report.id, 'pdf')}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      PDF
                    </button>
                    <button
                      onClick={() => handleDownload(report.id, 'latex')}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      LaTeX
                    </button>
                    {report.github_repository_url && (
                      <a
                        href={report.github_repository_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200"
                      >
                        GitHub
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* GitHub Reports */}
          {reports.github_reports.map((report, index) => (
            <div key={`github-${index}`} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">{report.name}</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Stored in GitHub • {report.files.tex?.size ? `${Math.round(report.files.tex.size / 1024)}KB` : 'Unknown size'}
                  </p>
                  <div className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      GitHub Archive
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  {report.files.pdf && (
                    <a
                      href={report.files.pdf.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200"
                    >
                      PDF
                    </a>
                  )}
                  {report.files.tex && (
                    <a
                      href={report.files.tex.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200"
                    >
                      LaTeX
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LaTeXReportsPanel;